import { createContext, useContext, useState, useEffect } from 'react'
import api, { organizationApi } from '../services/api'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [team, setTeam] = useState(null)
  const [teams, setTeams] = useState([])
  const [loading, setLoading] = useState(true)

  // Fetch all user's teams/organizations
  const fetchTeams = async () => {
    try {
      const response = await organizationApi.list()
      return response.data || []
    } catch (error) {
      console.error('Failed to fetch teams:', error)
      return []
    }
  }

  // Legacy alias
  const fetchTeam = fetchTeams

  // Select/set the team
  const selectTeam = (teamData) => {
    if (teamData) {
      localStorage.setItem('team', JSON.stringify(teamData))
      api.defaults.headers.common['X-Organization-ID'] = teamData.id
      setTeam(teamData)
    }
  }

  // Refresh current team data from the server
  const refreshTeam = async () => {
    try {
      const allTeams = await fetchTeams()
      setTeams(allTeams)

      if (team && allTeams.length > 0) {
        // Find the current team in the refreshed list
        const updatedTeam = allTeams.find(t => t.id === team.id)
        if (updatedTeam) {
          selectTeam(updatedTeam)
          return updatedTeam
        }
      }
      return null
    } catch (error) {
      console.error('Failed to refresh team:', error)
      return null
    }
  }

  useEffect(() => {
    const initAuth = async () => {
      // Check for saved user info (token is now in httpOnly cookie)
      const savedUser = localStorage.getItem('user')
      const savedTeam = localStorage.getItem('team') || localStorage.getItem('organization')

      if (savedUser) {
        setUser(JSON.parse(savedUser))

        // Fetch all teams to verify session is still valid
        try {
          const allTeams = await fetchTeams()
          setTeams(allTeams)

          if (allTeams.length > 0) {
            // Session is valid, set up team
            if (savedTeam) {
              const savedTeamData = JSON.parse(savedTeam)
              // Find the saved team in the list
              const matchingTeam = allTeams.find(t => t.id === savedTeamData.id)
              if (matchingTeam) {
                selectTeam(matchingTeam)
              } else {
                // Saved team no longer valid, use first team
                selectTeam(allTeams[0])
              }
            } else {
              selectTeam(allTeams[0])
            }
          } else {
            // No teams returned - might be logged out
            // Clear local state but let API interceptor handle 401
          }
        } catch (error) {
          // If fetch fails with 401, interceptor will clear state
          console.error('Session validation failed:', error)
          if (error.response?.status === 401) {
            localStorage.removeItem('user')
            localStorage.removeItem('team')
            setUser(null)
          }
        }

        // Clean up old localStorage key
        localStorage.removeItem('organization')
      }

      setLoading(false)
    }

    initAuth()
  }, [])

  const login = async (email, password) => {
    const response = await api.post('/api/auth/login', { email, password })
    const { access_token, user: userData } = response.data

    // Store token and user info in localStorage (for cross-origin support)
    if (access_token) {
      localStorage.setItem('token', access_token)
    }
    localStorage.setItem('user', JSON.stringify(userData))

    setUser(userData)

    // Fetch all teams and select first one
    const allTeams = await fetchTeams()
    setTeams(allTeams)
    if (allTeams.length > 0) {
      selectTeam(allTeams[0])
    }

    return userData
  }

  const register = async (email, password, name) => {
    const response = await api.post('/api/auth/register', { email, password, name })
    const { access_token, user: userData } = response.data

    // Store token and user info in localStorage (for cross-origin support)
    if (access_token) {
      localStorage.setItem('token', access_token)
    }
    localStorage.setItem('user', JSON.stringify(userData))

    setUser(userData)

    // Fetch all teams and select first one
    const allTeams = await fetchTeams()
    setTeams(allTeams)
    if (allTeams.length > 0) {
      selectTeam(allTeams[0])
    }

    return userData
  }

  const logout = async () => {
    try {
      // Call logout endpoint to clear httpOnly cookie
      await api.post('/api/auth/logout')
    } catch (error) {
      console.error('Logout error:', error)
    }

    // Clear local state
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    localStorage.removeItem('team')
    localStorage.removeItem('organization')
    delete api.defaults.headers.common['X-Organization-ID']
    setUser(null)
    setTeam(null)
    setTeams([])
  }

  // Set up session from token and user data (used after password reset)
  const setSession = async (access_token, userData) => {
    // Store token and user info in localStorage
    if (access_token) {
      localStorage.setItem('token', access_token)
    }
    localStorage.setItem('user', JSON.stringify(userData))

    setUser(userData)

    // Fetch all teams and select first one
    const allTeams = await fetchTeams()
    setTeams(allTeams)
    if (allTeams.length > 0) {
      selectTeam(allTeams[0])
    }

    return userData
  }

  // Permission helpers
  const canManageTeam = team?.role === 'admin'
  const canEditCampaigns = ['admin', 'editor'].includes(team?.role)
  const canView = ['admin', 'editor', 'viewer'].includes(team?.role)

  // Feature access helpers
  const featureAccess = team?.feature_access || 'both'
  const canAccessCampaigns = featureAccess === 'both' || featureAccess === 'campaigns'
  const canAccessDebriefs = featureAccess === 'both' || featureAccess === 'debriefs'

  const value = {
    user,
    team,
    teams,
    // Legacy compatibility
    organization: team,
    organizations: teams,
    loading,
    login,
    register,
    logout,
    setSession,
    selectTeam,
    selectOrganization: selectTeam,
    fetchOrganizations: fetchTeams,
    fetchTeam,
    fetchTeams,
    refreshTeam,
    isAuthenticated: !!user,
    // Permission helpers
    canManageTeam,
    canEditCampaigns,
    canView,
    // Feature access helpers
    featureAccess,
    canAccessCampaigns,
    canAccessDebriefs,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
